import RoofingNail from "@/components/RoofingNail";
import { FileText, ClipboardCheck } from "lucide-react";

const STEPS = [
  {
    n: "01",
    title: "Free Estimate",
    desc: "Schedule a site visit. We climb the roof, measure every plane, document every flashing — then send a clean estimate within 48 hours.",
    icon: "icon-clipboard",
  },
  {
    n: "02",
    title: "Custom Proposal",
    desc: "Material samples, layout drawings, warranty docs, transparent line-item pricing. No pressure, no commission games.",
    icon: "icon-file",
  },
  {
    n: "03",
    title: "Professional Installation",
    desc: "Certified crew, manufacturer-spec materials, daily cleanup. Most homes finished in one to three days.",
    icon: "nail",
  },
];

export default function Process() {
  return (
    <section
      data-testid="process-section"
      className="relative shingle-texture bg-[var(--crest-charcoal)] py-24 md:py-36 overflow-hidden"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-20">
          <div className="reveal flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-[var(--crest-amber)]" />
            <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
              The Process
            </span>
          </div>
          <h3
            data-testid="process-heading"
            className="reveal font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.5rem)" }}
          >
            Three steps.
            <br />
            <span className="text-white/50">Zero surprises.</span>
          </h3>
        </div>

        {/* Timeline — horizontal desktop with connector line, vertical mobile */}
        <div className="relative">
          {/* Desktop horizontal connector */}
          <div className="hidden md:block absolute top-[68px] left-[8%] right-[8%] h-px bg-gradient-to-r from-transparent via-[var(--crest-amber)]/40 to-transparent" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 relative">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                data-testid={`process-step-${i + 1}`}
                className="reveal relative flex flex-col items-start"
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                {/* Oversized watermark number */}
                <span
                  aria-hidden
                  className="absolute -top-12 -left-4 font-display font-extrabold text-[10rem] md:text-[12rem] leading-none text-white/[0.04] select-none pointer-events-none"
                >
                  {s.n}
                </span>

                {/* Icon / unexpected design move */}
                <div className="relative z-10 mb-8 h-[140px] flex items-end">
                  {s.icon === "icon-clipboard" && (
                    <div className="w-16 h-16 flex items-center justify-center bg-[#1a1a1a] border border-white/10">
                      <ClipboardCheck
                        size={28}
                        strokeWidth={1.5}
                        className="text-[var(--crest-amber)]"
                      />
                    </div>
                  )}
                  {s.icon === "icon-file" && (
                    <div className="w-16 h-16 flex items-center justify-center bg-[#1a1a1a] border border-white/10">
                      <FileText
                        size={28}
                        strokeWidth={1.5}
                        className="text-[var(--crest-amber)]"
                      />
                    </div>
                  )}
                  {s.icon === "nail" && (
                    <div className="relative -ml-4 -mb-4">
                      <RoofingNail size={170} className="drop-shadow-[0_8px_24px_rgba(217,119,6,0.35)]" />
                    </div>
                  )}
                </div>

                {/* Step label */}
                <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)] mb-3">
                  Step {s.n}
                </span>
                <h3 className="font-display uppercase font-extrabold text-2xl md:text-3xl text-white tracking-tight leading-tight mb-4">
                  {s.title}
                </h3>
                <p className="font-body text-sm text-white/65 leading-relaxed max-w-sm">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
